import { useEffect, useRef, useState } from 'react';

// THE HUMAN CHECK on sign-up (SIGNUP_MODE=phone): Cloudflare Turnstile,
// rendered explicitly from Cloudflare's own script, with no package between.
// Drawn only when the build carries a site key; without one (local
// development, admin-approved signup) it is nothing and nothing is sent.
// A token is single-use, so the page bumps `resetKey` after any refusal and
// the widget asks again.

const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SRC;
      script.async = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('no turnstile')));
      script.onerror = () => { loading = null; reject(new Error('turnstile did not load')); };
      document.head.appendChild(script);
    });
  }
  return loading;
}

/** The build's Turnstile site key, or '' when there is none. */
export const turnstileSiteKey = () => import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

function Turnstile({ siteKey, onToken, resetKey = 0 }) {
  const box = useRef(null);
  const widget = useRef(null);
  const report = useRef(onToken);
  const [failed, setFailed] = useState(false);

  useEffect(() => { report.current = onToken; }, [onToken]);

  useEffect(() => {
    if (!siteKey) return undefined;
    let live = true;
    loadTurnstile()
      .then((ts) => {
        if (!live || !box.current) return;
        widget.current = ts.render(box.current, {
          sitekey: siteKey,
          callback: (token) => report.current?.(token),
          'expired-callback': () => report.current?.(null),
          'error-callback': () => report.current?.(null),
        });
      })
      .catch(() => { if (live) setFailed(true); });
    return () => {
      live = false;
      if (widget.current != null) {
        try { window.turnstile?.remove(widget.current); } catch { /* already gone */ }
        widget.current = null;
      }
    };
  }, [siteKey]);

  useEffect(() => {
    if (!resetKey || widget.current == null) return;
    report.current?.(null);
    try { window.turnstile?.reset(widget.current); } catch { /* re-rendered next mount */ }
  }, [resetKey]);

  if (!siteKey) return null;
  return (
    <>
      <div ref={box} className="account-turnstile" />
      {failed && (
        <p className="account-error" role="alert">
          The human check could not load. Check your connection and reload the page.
        </p>
      )}
    </>
  );
}

export default Turnstile;
